// SEO data and structured data helpers
import buildWalkUrl from './walkUrl';

export const generateWalkSchema = (walk) => {
  const siteUrl = process.env.REACT_APP_SITE_URL || 'https://wanderloop.com';
  const walkUrl = `${siteUrl}${buildWalkUrl(walk)}`;

  // Parse distance in meters from the display string (e.g. "14.0 miles")
  const distanceMatch = walk.distance?.match(/([\d.]+)/);
  const distanceMiles = distanceMatch ? parseFloat(distanceMatch[1]) : null;
  const distanceKm = distanceMiles ? (distanceMiles * 1.60934).toFixed(1) : null;

  // Parse duration minutes from display string (e.g. "45 min" or "1h 30m")
  const durationMatch = walk.duration?.match(/(?:(\d+)h)?\s*(?:(\d+)\s*m)?/);
  const durationMinutes = durationMatch
    ? (parseInt(durationMatch[1] || 0) * 60) + parseInt(durationMatch[2] || 0)
    : null;
  const durationISO = durationMinutes ? `PT${durationMinutes}M` : null;

  const schema = [
    // TouristAttraction — the walk itself
    {
      "@context": "https://schema.org",
      "@type": "TouristAttraction",
      "name": walk.title,
      "description": walk.practicalSummary || walk.description,
      "image": walk.photos?.length > 0 ? walk.photos : undefined,
      "url": walkUrl,
      ...(walk.startingPoint && walk.startingPoint !== 'Unknown' ? {
        "address": {
          "@type": "PostalAddress",
          "streetAddress": walk.startingPoint,
          ...(walk.region ? { "addressLocality": walk.region } : {}),
        }
      } : {}),
      ...(walk.startingCoords?.lat ? {
        "geo": {
          "@type": "GeoCoordinates",
          "latitude": walk.startingCoords.lat,
          "longitude": walk.startingCoords.lng
        }
      } : {}),
      ...(walk.userRatings > 0 ? {
        "aggregateRating": {
          "@type": "AggregateRating",
          "ratingValue": walk.userRatings,
          "ratingCount": walk.ratingCount || 1,
          "bestRating": 5
        }
      } : {}),
      "isAccessibleForFree": true,
      "publicAccess": true,
      "touristType": "Walking",
      "category": walk.category,
      "keywords": [
        "walking route",
        "urban walk",
        walk.category?.toLowerCase(),
        walk.difficulty?.toLowerCase(),
        walk.region?.toLowerCase(),
      ].filter(Boolean).join(", ")
    },

    // ExerciseAction — the walking activity
    {
      "@context": "https://schema.org",
      "@type": "ExerciseAction",
      "name": `Walk: ${walk.title}`,
      "description": walk.practicalSummary || walk.description,
      "url": walkUrl,
      "exerciseType": "Walking",
      ...(distanceKm ? {
        "distance": {
          "@type": "QuantitativeValue",
          "value": distanceKm,
          "unitCode": "KMT"
        }
      } : {}),
      ...(durationISO ? { "duration": durationISO } : {}),
      ...(walk.startingPoint && walk.startingPoint !== 'Unknown' ? {
        "fromLocation": {
          "@type": "Place",
          "name": walk.startingPoint,
          ...(walk.startingCoords?.lat ? {
            "geo": {
              "@type": "GeoCoordinates",
              "latitude": walk.startingCoords.lat,
              "longitude": walk.startingCoords.lng
            }
          } : {})
        }
      } : {}),
      ...(walk.endingPoint && walk.endingPoint !== 'Unknown' ? {
        "toLocation": {
          "@type": "Place",
          "name": walk.endingPoint,
          ...(walk.endingCoords?.lat ? {
            "geo": {
              "@type": "GeoCoordinates",
              "latitude": walk.endingCoords.lat,
              "longitude": walk.endingCoords.lng
            }
          } : {})
        }
      } : {}),
      ...(walk.difficulty ? {
        "exerciseCourse": {
          "@type": "ExerciseCourse",
          "name": walk.title,
          ...(distanceKm ? {
            "distance": {
              "@type": "QuantitativeValue",
              "value": distanceKm,
              "unitCode": "KMT"
            }
          } : {})
        }
      } : {})
    }
  ];

  return schema;
};

export const generateBreadcrumbSchema = (breadcrumbs) => {
  const siteUrl = process.env.REACT_APP_SITE_URL || 'https://wanderloop.com';
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": breadcrumbs.map((breadcrumb, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "name": breadcrumb.name,
      "item": breadcrumb.url.startsWith('http') ? breadcrumb.url : `${siteUrl}${breadcrumb.url}`
    }))
  };
};

export const generateLocalBusinessSchema = () => {
  return {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "name": "Wanderloop",
    "description": "Curated urban walks and hiking trails in Los Angeles. Discover scenic routes, mindful walking experiences, and wellness on foot.",
    "url": process.env.REACT_APP_SITE_URL || 'https://wanderloop.com',
    "telephone": "+1-555-WANDER",
    "email": "bsmith@gmail.com",
    "address": {
      "@type": "PostalAddress",
      "addressLocality": "Los Angeles",
      "addressRegion": "CA",
      "addressCountry": "US"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": "34.0522",
      "longitude": "-118.2437"
    },
    "serviceArea": {
      "@type": "GeoCircle",
      "geoMidpoint": {
        "@type": "GeoCoordinates",
        "latitude": "34.0522",
        "longitude": "-118.2437"
      },
      "geoRadius": "50000"
    },
    "priceRange": "Free",
    "openingHours": "Mo-Su 00:00-23:59"
  };
};

export const seoKeywords = {
  walks: [
    'Los Angeles walks',
    'LA hiking trails',
    'urban walks Los Angeles',
    'scenic walks LA',
    'walking routes Los Angeles',
    'best walks in LA',
    'Los Angeles walking tours',
    'urban hiking LA',
    'walking trails near me',
    'LA outdoor activities'
  ],
  categories: {
    'Nature & Parks': ['nature walks LA', 'park trails Los Angeles', 'green spaces LA'],
    'Secret LA': ['hidden gems LA', 'secret spots Los Angeles', 'off beaten path LA'],
    'Architecture': ['architectural walks LA', 'building tours Los Angeles', 'design walks LA'],
    'Food-Focused Walks': ['food tours LA', 'culinary walks Los Angeles', 'restaurant walks LA'],
    'Long-Distance Treks': ['long hikes LA', 'challenging walks Los Angeles', 'endurance trails LA'],
    'Historic LA': ['historical walks LA', 'heritage tours Los Angeles', 'historic sites LA'],
    'Family-Friendly': ['family walks LA', 'kid friendly trails Los Angeles', 'easy walks LA'],
    'Sunset Walks': ['sunset views LA', 'evening walks Los Angeles', 'golden hour LA']
  }
};

export const generateItemListSchema = (walks, listName) => {
  const siteUrl = process.env.REACT_APP_SITE_URL || 'https://wanderloop.com';
  return {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "name": listName || "Walks",
    "numberOfItems": walks.length,
    "itemListElement": walks.map((walk, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "name": walk.title,
      "url": `${siteUrl}${buildWalkUrl(walk)}`,
    })),
  };
};

export const generateWebSiteSchema = () => {
  const siteUrl = process.env.REACT_APP_SITE_URL || 'https://wanderloop.com';
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": "Wanderloop",
    "url": siteUrl,
    "potentialAction": {
      "@type": "SearchAction",
      "target": {
        "@type": "EntryPoint",
        "urlTemplate": `${siteUrl}/walks?q={search_term_string}`,
      },
      "query-input": "required name=search_term_string",
    },
  };
};

export const getWalkKeywords = (walk) => {
  const region = walk.region || '';
  const baseKeywords = [
    walk.title.toLowerCase(),
    walk.startingPoint ? `${walk.startingPoint} walk` : null,
    region ? `${walk.category.toLowerCase()} ${region.toLowerCase()}` : walk.category.toLowerCase(),
    `${walk.difficulty.toLowerCase()} walks`,
    'urban walking',
    'scenic routes',
    region ? `walks in ${region.toLowerCase()}` : null,
  ].filter(Boolean);

  const categoryKeywords = seoKeywords.categories[walk.category] || [];

  return [...baseKeywords, ...categoryKeywords].join(', ');
};